(function () {
  // Infinite scroll for collection pages (loaded by sections/main-collection.liquid when
  // the "infinite_scroll" section setting is on). Fetches `?page=N&view=ajax`
  // (templates/collection.ajax.liquid) and appends the product cards to the current grid.
  //
  // State is rebuilt whenever the theme re-renders the results (filter / sort / back
  // button): theme.js replaces #CollectionAjaxContent and dispatches `collection:reloaded`.

  const debounce = function(func, wait) {
    let timeout;
    return function() {
      const context = this, args = arguments;
      clearTimeout(timeout);
      timeout = setTimeout(function() {
        func.apply(context, args);
      }, wait);
    };
  };

  const init = function() {
    if (!window.location.pathname.includes('/collections/')) return;

    const MAX_RETRIES = 3;
    const CONTENT_ID = 'CollectionAjaxContent';

    let generation = 0;     // bumped on every re-render; in-flight fetches from an older generation are dropped
    let nextPage = 2;
    let isLoading = false;
    let retryCount = 0;
    let scrollBound = false;
    let loadingElement = null;

    const contentRoot = function() {
      return document.getElementById(CONTENT_ID) || document;
    };

    // Page the current URL is on (old Google results / shared links still point at ?page=2).
    const currentPage = function() {
      const page = parseInt(new URLSearchParams(window.location.search).get('page'), 10);
      return page > 0 ? page : 1;
    };

    // Next-page URL built from the current URL so filters / sort_by are preserved.
    const buildPageUrl = function(page) {
      const params = new URLSearchParams(window.location.search);
      params.set('page', page);
      params.set('view', 'ajax');
      return window.location.pathname + '?' + params.toString();
    };

    // URL of page 1 with the current filters / sort kept.
    const firstPageUrl = function() {
      const params = new URLSearchParams(window.location.search);
      params.delete('page');
      const query = params.toString();
      return window.location.pathname + (query ? '?' + query : '');
    };

    // The product grid the fetched cards are appended to (collection grid only — the page
    // has other product grids, e.g. featured collections).
    const findGrid = function() {
      const root = contentRoot();
      const selectors = [
        '.grid.product-wrapper[js-product-grids]',
        '[data-scroll-to] .grid.grid--uniform',
        '.grid.grid--uniform'
      ];
      for (const selector of selectors) {
        const grid = root.querySelector(selector);
        if (grid) return grid;
      }
      return null;
    };

    const findPagination = function() {
      return contentRoot().querySelector('.pagination');
    };

    const removeRetryButton = function() {
      const retryButton = contentRoot().querySelector('.infinite-scroll-retry');
      if (retryButton) retryButton.remove();
    };

    const showLoading = function(show) {
      if (loadingElement) loadingElement.style.display = show ? 'block' : 'none';
    };

    const unbindScroll = function() {
      window.removeEventListener('scroll', onScroll);
      scrollBound = false;
    };

    const bindScroll = function() {
      if (scrollBound) return;
      window.addEventListener('scroll', onScroll);
      scrollBound = true;
    };

    const loadNextPage = function() {
      if (isLoading) return;
      isLoading = true;
      const gen = generation;
      const page = nextPage;

      showLoading(true);
      removeRetryButton();

      fetch(buildPageUrl(page))
        .then(function(response) {
          if (!response.ok) throw new Error('Network response was not ok');
          return response.text();
        })
        .then(function(html) {
          if (gen !== generation) return; // results were re-rendered meanwhile — drop this page

          const tempContainer = document.createElement('div');
          tempContainer.innerHTML = html;
          const newProducts = Array.from(tempContainer.querySelectorAll('.grid__item.grid-product'));
          const grid = findGrid();

          if (newProducts.length > 0 && grid) {
            newProducts.forEach(function(product) { grid.appendChild(product); });
            nextPage = page + 1;
            retryCount = 0;
            if (typeof theme !== 'undefined' && typeof theme.reinitProductGridItem === 'function') {
              theme.reinitProductGridItem();
            }
            if (!tempContainer.querySelector('.pagination .next a')) {
              unbindScroll(); // that was the last page
            }
          } else {
            unbindScroll(); // nothing more to load
          }

          isLoading = false;
          showLoading(false);
        })
        .catch(function(error) {
          if (gen !== generation) return;
          console.error('Infinite scroll: error loading page ' + page, error);
          isLoading = false;
          showLoading(false);

          if (retryCount < MAX_RETRIES && loadingElement && loadingElement.parentNode) {
            const retryButton = document.createElement('button');
            retryButton.type = 'button';
            retryButton.className = 'infinite-scroll-retry';
            retryButton.innerText = 'Load More Products';
            retryButton.style.cssText = 'display:block; margin:20px auto; padding:10px 20px; background:#000; color:#fff; border:none; border-radius:4px; cursor:pointer;';
            retryButton.onclick = function() {
              this.remove();
              retryCount++;
              setTimeout(loadNextPage, Math.min(1000 * Math.pow(2, retryCount - 1), 10000));
            };
            loadingElement.parentNode.insertBefore(retryButton, loadingElement.nextSibling);
          }
        });
    };

    const onScroll = debounce(function() {
      if (document.hidden || isLoading) return;
      const scrollPosition = window.scrollY + window.innerHeight;
      const threshold = document.documentElement.scrollHeight - (window.innerWidth <= 768 ? 1200 : 500);
      if (scrollPosition > threshold) loadNextPage();
    }, 150);

    // "View earlier products" link when the shopper lands on ?page=2+ (pagination is hidden,
    // so page 1 would otherwise be unreachable).
    const addEarlierLink = function(grid) {
      if (currentPage() <= 1 || !grid || contentRoot().querySelector('.infinite-scroll-earlier')) return;
      const link = document.createElement('a');
      link.className = 'infinite-scroll-earlier';
      link.href = firstPageUrl();
      link.innerText = 'View earlier products';
      link.style.cssText = 'display:block; width:max-content; margin:0 auto 20px; padding:10px 24px; border:2px solid #282828; border-radius:64px; color:#282828; font-weight:700; text-decoration:none;';
      grid.parentNode.insertBefore(link, grid);
    };

    // (Re)attach to the results currently in the DOM. Called on load and after every
    // theme re-render (filter / sort / back button).
    const attach = function() {
      generation++;
      isLoading = false;
      retryCount = 0;
      nextPage = currentPage() + 1;
      unbindScroll();
      removeRetryButton();
      if (loadingElement && loadingElement.parentNode) loadingElement.remove();

      const grid = findGrid();
      const pagination = findPagination();
      addEarlierLink(grid);

      if (!grid || !pagination) return; // single page of results: nothing to load

      pagination.style.display = 'none';

      if (!loadingElement) {
        loadingElement = document.createElement('h4');
        loadingElement.innerText = 'Loading...';
        loadingElement.style.textAlign = 'center';
        loadingElement.style.margin = '20px 0';
        loadingElement.classList.add('loading-animation');
      }
      loadingElement.style.display = 'none';
      pagination.parentNode.insertBefore(loadingElement, pagination);

      if (!pagination.querySelector('.next a')) return; // landed on the last page

      bindScroll();
      onScroll(); // short first pages: load the next one right away if already past the threshold
    };

    const style = document.createElement('style');
    style.innerHTML = '@keyframes flash{0%{opacity:1}50%{opacity:.5}100%{opacity:1}}' +
      '.loading-animation{animation:flash .5s infinite}' +
      '.infinite-scroll-retry:hover{opacity:.8}' +
      '.infinite-scroll-earlier:hover{background:#FFED52}';
    document.head.appendChild(style);

    document.addEventListener('visibilitychange', function() {
      if (!document.hidden) onScroll();
    });
    if ('connection' in navigator) {
      navigator.connection.addEventListener('change', function() {
        if (navigator.onLine) onScroll();
      });
    }

    document.addEventListener('collection:reloaded', attach);

    attach();
  };

  // Run now if the DOM is already parsed (script may land after DOMContentLoaded), otherwise wait for it.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
